import { safeGradient } from "@/shared/utils/gradient-css";
import { isGradient } from "@components/color-picker/lib/detect";
import {
  MEDIA_BLOB_SHAPE,
  resolveParallelogramMask,
} from "@library/constants";
import styled, { css } from "styled-components";
import {
  buildMediaBoxShadow,
  buildMediaDropShadow,
} from "../../../shared/build-media-shadow";

interface VideoWrapperProps {
  $verticalAlignment: string;
  $horizontalAlignment: string;
  $hasDynamicVariable?: boolean;
}

interface VideoLayoutProps {
  $round: string;
  $shape: string;
  $shadow: string;
  $shadowColor?: string | "design-system";
  $shadowOpacity?: number | "design-system";
  $height: number;
  $preview: boolean;
  $blobType?: string;
  $parallelogramDirection?: string;
  $overlayOpacity?: number | "design-system";
  $overlayColor?: string;
  $filter?: boolean | "design-system";
  $isShort?: boolean;
}

interface VideoElementProps {
  $round: string;
  $blobType?: string;
  $parallelogramDirection?: string;
  $shape?: string;
}

export const StyledVideoWrapper = styled.div<VideoWrapperProps>`
  align-items: ${({ $verticalAlignment }) => $verticalAlignment || "inherit"};
  justify-content: ${({ $horizontalAlignment }) =>
    $horizontalAlignment || "inherit"};
`;

export const StyledVideoLayout = styled.div<VideoLayoutProps>`
  &.videoLayout {
    border-radius: ${({ $round }) => ($round ? $round : "0")};
    width: var(--entity-size, 100%);
    /* Mobile: when capped, use shape-based cap (ignore user resize override). When uncapped, use min(user resize, original). Combo width (e.g. 30% for SideBySide 1/3) is always a hard ceiling. When --combo-mobile-fill-cell (array 3+ per line), ignore entity-size so media fills cell. The whole min() is floored by --entity-mobile-min-width so stacked caps never shrink media to nothing. */
    @media screen and (max-width: 768px) {
      width: max(
        var(--entity-mobile-min-width, 40%),
        min(
          calc(
            var(--entity-mobile-width-override, 100%) *
              (1 - var(--combo-mobile-media-capped, 0)) + 100% *
              var(--combo-mobile-media-capped, 0)
          ),
          calc(
            100% * (1 - var(--combo-mobile-media-capped, 0)) +
              var(--entity-mobile-cap-by-shape, 40%) *
              var(--combo-mobile-media-capped, 0)
          ),
          calc(
            var(--entity-size, 100%) * (1 - var(--combo-mobile-fill-cell, 0)) +
              100% * var(--combo-mobile-fill-cell, 0)
          ),
          calc(
            var(--entity-size, 100%) * var(--combo-mobile-media-ratio, 1) *
              (1 - var(--combo-mobile-fill-cell, 0)) + 100% *
              var(--combo-mobile-fill-cell, 0)
          )
        )
      ) !important;
    }
    ${({ $shadow, $shape = "", $shadowColor, $shadowOpacity }) => {
      if (
        !$shadow ||
        $shadow === "none" ||
        ["blob", "parallelogram"].includes($shape)
      )
        return;
      return css`
        box-shadow: ${buildMediaBoxShadow(
          $shadow,
          $shadowColor,
          $shadowOpacity,
        )};
      `;
    }}

    /* Overlay styling */
    .video__container::after {
      opacity: ${({ $overlayOpacity }) =>
        $overlayOpacity === "design-system"
          ? "calc(var(--media-overlay-opacity, 0) * 1%)"
          : `calc(${$overlayOpacity} * 1%)`};
      background: ${({ $overlayColor }) => {
        if ($overlayColor === "design-system")
          return "var(--media-overlay-color, #FFFFFF)";
        if (!$overlayColor) return "#FFFFFF";
        if (isGradient($overlayColor)) {
          return safeGradient($overlayColor) ?? "#FFFFFF";
        }
        return $overlayColor;
      }};
      border-radius: ${({ $round = "0" }) =>
        setDesignSystemVariable($round, "media-corner-radius") || "0"};
      ${(props) => handleMask(props)}
    }

    ${({ $shape }) =>
      $shape === "circle" &&
      css`
        border-radius: 100% !important;
        .video__container {
          border-radius: 100% !important;
          padding-top: 100%;
        }
      `};
    ${({ $shape }) =>
      ($shape === "square" ||
        $shape === "blob" ||
        $shape === "parallelogram") &&
      css`
        .video__container {
          border-radius: 0 !important;
          padding-top: 100%;
        }
      `};
    ${({ $shape }) =>
      $shape === "16-9" &&
      css`
        .video__container {
          padding-top: 56.25%;
        }
      `};
    ${({ $shape }) => {
      if ($shape === "free" || !$shape)
        return css`
          .video__container {
            video,
            img,
            iframe {
              position: static;
              display: block;
              object-fit: contain;

              &[data-has-aspect-ratio="true"] {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
              }
            }

            .vidyard-player-container {
              height: 100% !important;
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;

              & > div {
                height: 100% !important;
              }

              & > a {
                position: absolute !important;
                bottom: 8px !important;
                left: 8px !important;
              }
              iframe {
                iframe {
                  z-index: 1;
                }
              }
            }

            .iframeWrapper {
              max-width: 100%;
              padding-top: 56.25%;
              position: relative;
              width: 100%;

              &[data-video-type="loom"] {
                padding-top: 64.99%;
              }

              iframe,
              img {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
              & + .noVideo {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
            }
          }
        `;
    }};
    ${({ $isShort, $shape }) =>
      $isShort &&
      ($shape === "free" || !$shape) &&
      css`
        .video__container .iframeWrapper {
          padding-top: 0;
          aspect-ratio: 9 / 16;
          width: 56.25%; /* 9/16 to maintain portrait ratio within container */
          margin: 0 auto;

          iframe,
          img {
            position: relative;
          }
        }
      `};
    ${({ $shape, $height }) =>
      $shape === "axis-resize" &&
      css`
        .video__container {
          height: ${$height ? $height + "px" : "auto"};
          padding-top: ${$height ? "unset" : "56.25%"};

          .noVideo {
            position: relative !important;
            height: ${$height ? $height + "px" : "auto"};
            padding-top: ${$height ? "unset" : "56.25%"};
          }
        }
      `};
    .iframeWrapper {
      &::before {
        filter: ${({ $round, $shape }) =>
          $round
            ? $shape === "square"
              ? "blur(0)"
              : "blur(" + $round + ")"
            : "none"};
      }
      iframe {
        border-radius: ${({ $round }) => ($round ? $round : "0")};
        clip-path: ${({ $round }) =>
          $round ? "inset(0% 0% 0% 0% round " + $round + ")" : "none"};
      }
    }

    iframe,
    img {
      ${(props) => handleMask(props)}
    }

    .embedded-bg-cL5dyjnKIl img {
      mask: none !important;
    }

    video {
      /* Handle black and white filter */
      ${({ $filter }) =>
        $filter === "design-system"
          ? css`
              filter: var(--media-black-and-white-filter, grayscale(0));
            `
          : $filter &&
            css`
              filter: grayscale(1);
            `}
    }

    ${({ $shadow, $shape, $shadowColor, $shadowOpacity }) => {
      if (
        !$shadow ||
        $shadow === "none" ||
        !["blob", "parallelogram"].includes($shape)
      )
        return;
      return css`
        filter: ${buildMediaDropShadow($shadow, $shadowColor, $shadowOpacity)};
      `;
    }}
  }
`;

export const StyledVideoElement = styled.video<VideoElementProps>`
  border-radius: ${({ $round }) => ($round ? $round : "0")};
  ${({ $round }) =>
    ["100px", "100%"].includes($round) &&
    css`
      border-radius: 100%;
      height: 100%;
    `};

  ${(props) => handleMask(props)}
`;

const handleMask = (props: {
  $blobType?: string;
  $parallelogramDirection?: string;
  $shape?: string;
  $round?: string;
}) => {
  const { $blobType, $parallelogramDirection, $shape, $round } = props;
  if ($shape === "blob") {
    const mask = MEDIA_BLOB_SHAPE[$blobType || "blob-1"];
    return css`
      mask-image: ${mask};
      mask-repeat: no-repeat;
      mask-position: center;
      mask-size: cover;
    `;
  }

  if ($shape === "parallelogram") {
    const mask = resolveParallelogramMask($parallelogramDirection, $round);
    return css`
      mask-image: ${mask};
      mask-repeat: no-repeat;
      mask-position: center;
      mask-size: cover;
    `;
  }

  return "";
};

const setDesignSystemVariable = (
  value: string | number | "design-system",
  variable: string,
) => {
  if (value === "design-system") return `var(--${variable})`;
  return value;
};
